import { Intersects } from "./Intersects";

export class Rectangle {
    x: number;
    y: number;
    width: number;
    height: number;

    /**
     * The x coordinate of the left of the Rectangle.
     * Changing the left property of a Rectangle object has no effect on the y and height properties. However it does affect the width property, whereas changing the x value does not affect the width property.
     */
    get left(): number { return this.x; }
    set left(val: number) {
        if (val >= this.right) {
            this.width = 0;
        }
        else {
            this.width = this.right - val;
        }

        this.x = val;
    }

    /**
     * The sum of the x and width properties.
     * Changing the right property of a Rectangle object has no effect on the x, y and height properties, however it does affect the width property.
     */
    get right(): number { return this.x + this.width; }
    set right(val: number) {
        if (val <= this.x) {
            this.width = 0;
        }
        else {
            this.width = val - this.x;
        }
    }

    /**
     * The y coordinate of the top of the Rectangle. Changing the top property of a Rectangle object has no effect on the x and width properties.
     * However it does affect the height property, whereas changing the y value does not affect the height property.
     */
    get top(): number { return this.y; }
    set top(val: number) {
        if (val >= this.bottom) {
            this.height = 0;
        }
        else {
            this.height = (this.bottom - val);
        }

        this.y = val;
    }

    /**
     * The sum of the y and height properties.
     * Changing the bottom property of a Rectangle object has no effect on the x, y and width properties, but does change the height property.
     */
    get bottom(): number { return this.y + this.height; }
    set bottom(val: number) {
        if (val <= this.y) {
            this.height = 0;
        }
        else {
            this.height = val - this.y;
        }
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor(x: number = 0, y: number = 0, w: number = 0, h: number = 0) {
        this.x = x;
        this.y = y;
        this.width = w;
        this.height = h;
    }

    //-------------------------------------------------------------------------
    /**
     * Sets the position, width, and height of the Rectangle.
     * @param x 
     * @param y 
     * @param w 
     * @param h 
     */
    //-------------------------------------------------------------------------
    setTo(x: number, y: number, w: number, h: number): Rectangle {
        this.x = x;
        this.y = y;
        this.width = w;
        this.height = h;

        return this;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    setFrom(rc: Rectangle): Rectangle {
        this.x = rc.x;
        this.y = rc.y;
        this.width = rc.width;
        this.height = rc.height;

        return this;
    }

    //-------------------------------------------------------------------------
    /**
     * Resets the position, width, and height of the Rectangle to 0.
     */
    //-------------------------------------------------------------------------
    setEmpty(): Rectangle {
        this.setTo(0, 0, 0, 0);
        return this;
    }

    //-------------------------------------------------------------------------
    /**
     * Sets the position of the Rectangle.
     * @param x 
     * @param y 
     */
    //-------------------------------------------------------------------------
    setPosition(x: number, y: number): Rectangle {
        this.x = x;
        this.y = y;

        return this;
    }

    //-------------------------------------------------------------------------
    /**
     * Determines if the Rectangle is empty. A Rectangle is empty if its width or height is less than or equal to 0.
     */
    //-------------------------------------------------------------------------
    isEmpty(): boolean {
        return (this.width <= 0 || this.height <= 0);
    }

    //-------------------------------------------------------------------------
    /**
     * Checks if the given point is inside the Rectangle's bounds.
     * @param x 
     * @param y 
     */
    //-------------------------------------------------------------------------
    contains(x: number, y: number): boolean {
        return Rectangle.Contains(this, x, y);
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    move(x: number, y: number): Rectangle {
        this.x += x;
        this.y += y;

        return this;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    intersect(rect: Rectangle): boolean {
        return Intersects.RectangleToRectangle(this, rect);
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    intersection(rect: Rectangle, out?: Rectangle): Rectangle {
        return Rectangle.Intersection(this, rect, out);
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    static Contains(rect: Rectangle, x: number, y: number): boolean {
        if (rect.width <= 0 || rect.height <= 0)
            return false;

        return (rect.x <= x && rect.x + rect.width >= x && rect.y <= y && rect.y + rect.height >= y);
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    static Intersection(rectA: Rectangle, rectB: Rectangle, out: Rectangle = new Rectangle()): Rectangle {
        if (Intersects.RectangleToRectangle(rectA, rectB)) {
            out.x = Math.max(rectA.x, rectB.x);
            out.y = Math.max(rectA.y, rectB.y);
            out.width = Math.min(rectA.right, rectB.right) - out.x;
            out.height = Math.min(rectA.bottom, rectB.bottom) - out.y;
        }
        else {
            out.setEmpty();
        }

        return out;
    }
}